import React, { useState, useCallback, useEffect } from 'react';
import { Label } from 'components/UI/V3/Forms/Label/Label';
import DescriptionText from 'components/UI/V3/DescriptionText/DescriptionText';
import PriceField from 'components/UI/V3/Forms/PriceField';
import styles from './WalkingPriceField.module.scss';

interface WalkingPriceFieldProps {
    price: number;
    onPriceChange: (price: number) => void;
    onErrorChange?: (error: string | null) => void;
}

export const WalkingPriceField: React.FC<WalkingPriceFieldProps> = ({ price, onPriceChange, onErrorChange }) => {
    const [error, setError] = useState<string | null>(null);
    const [touched, setTouched] = useState(false);

    const validatePrice = useCallback((value: number): string | null => {
        if (value <= 0) {
            return 'Price must be greater than 0';
        }
        return null;
    }, []);

    const handleBlur = useCallback(() => {
        setTouched(true);
        const validationError = validatePrice(price);
        setError(validationError);
        if (onErrorChange) {
            onErrorChange(validationError);
        }
    }, [price, validatePrice, onErrorChange]);

    const handleChange = useCallback(
        (value: number | undefined) => {
            const numValue = value || 0;
            onPriceChange(numValue);
            if (touched) {
                const validationError = validatePrice(numValue);
                setError(validationError);
                if (onErrorChange) {
                    onErrorChange(validationError);
                }
            }
        },
        [touched, validatePrice, onPriceChange, onErrorChange]
    );

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <Label required>Fixed Price</Label>
            </div>

            <div className={styles.priceFieldWrapper}>
                <PriceField
                    scale={2}
                    value={price}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="0.00"
                    className={styles.priceInput}
                />
            </div>
            {touched && error && <div className={styles.error}>{error}</div>}
        </div>
    );
};
